import { motion } from 'framer-motion';
import { TreeDeciduous } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { cn } from '@/lib/utils';
import { EvolvingTree } from '../CarbonOffsetPanel/EvolvingTree';
import { useTreeGrowth } from '../CarbonOffsetPanel/useTreeGrowth';

interface CarbonOffsetMiniProps {
  /** CO2 reduced in metric tons */
  /** null = no measured CO2 for the range (renders N/A) */
  co2MetricTons: number | null;
  /** Whether live demo mode is active */
  isLive?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Compact carbon offset panel for the 2x2 grid
 * Shows animated evolving tree with tree count and CO2 captured amount
 */
export function CarbonOffsetMini({
  co2MetricTons = 0,
  isLive = false,
  className,
}: CarbonOffsetMiniProps) {
  // null = honest absence: keep the tree at its seed stage and label the
  // figures N/A rather than presenting a measured-looking zero.
  const hasCO2 = typeof co2MetricTons === 'number' && !isNaN(co2MetricTons);
  const safeCO2Value = hasCO2 ? co2MetricTons : 0;
  const { treesSaved, stage, leafCount, trunkScale, canopyScale } = useTreeGrowth(safeCO2Value);

  return (
    <Card className={cn('flex flex-col h-full overflow-hidden relative', className)}>
      <div className="absolute top-1.5 right-2 z-10">
        <InfoTooltip
          content={
            <div className="space-y-1">
              <p className="font-semibold">Carbon Offset</p>
              <p>Environmental impact visualization showing the equivalent number of trees needed to absorb the CO₂ emissions prevented.</p>
              <p className="text-muted-foreground">One tree absorbs approximately 48 pounds of CO₂ per year. The animated tree grows as your fleet saves more carbon.</p>
            </div>
          }
        />
      </div>
      <CardContent className="flex-1 flex flex-col items-center justify-between p-2 gap-0">
        {/* Header - left aligned */}
        <div className="flex items-center gap-1 w-full">
          <TreeDeciduous className="h-3.5 w-3.5 text-[#8FB560]" />
          <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Carbon Offset
          </h3>
        </div>

        {/* Evolving Tree SVG - scaled down for mini panel */}
        <div className="flex-1 flex items-center justify-center w-full min-h-[50px] max-h-[70px]">
          <EvolvingTree
            stage={stage}
            leafCount={Math.min(leafCount, 35)} // Cap leaves for smaller panel
            trunkScale={trunkScale}
            canopyScale={canopyScale * 0.85} // Slightly smaller canopy
          />
        </div>

        {/* Statistics */}
        <div className="text-center space-y-0 w-full h-[52px]">
          {/* Trees Saved Count */}
          <div className="flex items-baseline justify-center gap-1">
            <motion.span
              key={treesSaved}
              className={cn(
                'text-lg font-bold text-[#8FB560] tabular-nums',
                isLive && 'animate-pulse'
              )}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.3 }}
            >
              {hasCO2 ? treesSaved.toLocaleString() : 'N/A'}
            </motion.span>
            <span className="text-[10px] text-muted-foreground">{hasCO2 ? 'Trees' : 'no data'}</span>
          </div>

          {/* CO2 Amount */}
          <motion.p
            className="text-[9px] text-muted-foreground/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
          >
            {hasCO2 ? `${safeCO2Value.toFixed(2)}t CO₂` : '— CO₂'}
          </motion.p>
        </div>
      </CardContent>
    </Card>
  );
}
